function titleCase(word: string): string {
  return word.length === 0 ? word : word[0]?.toUpperCase() + word.slice(1).toLowerCase();
}

/** `[1m]`, Claude Code's suffix for a model run with a larger context window. */
function contextSuffix(size: string | undefined, unit: string | undefined): string {
  return size === undefined || unit === undefined ? '' : ` (${size}${unit.toUpperCase()} context)`;
}

/**
 * How a model id reads to people: `claude-sonnet-4-6` is "Claude Sonnet 4.6",
 * `claude-opus-5[1m]` "Claude Opus 5 (1M context)", `gpt-5.5-codex` "GPT-5.5-Codex". An id it
 * does not recognise comes back as it is: a name is never made up.
 */
export function modelDisplayName(id: string): string {
  const trimmed = id.trim();
  // Cloud providers prefix and version Anthropic's ids: `us.anthropic.claude-…-v1:0`.
  const bare = trimmed
    .replace(/^(?:[a-z]{2,4}\.)?anthropic[./]/i, '')
    .replace(/-v\d+(?::\d+)?$/i, '');
  const claude = /^claude-([a-z]+)-(\d+)(?:[-.](\d{1,2}))?(?:-\d{8})?(?:\[(\d+)([km])\])?$/i.exec(
    bare,
  );
  if (claude) {
    const [, family = '', major, minor, size, unit] = claude;
    const version = minor === undefined ? major : `${major}.${minor}`;
    return `Claude ${titleCase(family)} ${version}${contextSuffix(size, unit)}`;
  }
  // The older order: `claude-3-5-sonnet-20241022`.
  const legacy = /^claude-(\d+)(?:-(\d))?-([a-z]+)(?:-\d{8})?$/i.exec(bare);
  if (legacy) {
    const [, major, minor, family = ''] = legacy;
    return `Claude ${titleCase(family)} ${minor === undefined ? major : `${major}.${minor}`}`;
  }
  const gpt = /^gpt-(\d[\w.]*)((?:-[a-z][\w.]*)*)$/i.exec(trimmed);
  if (gpt) {
    const [, version = '', rest = ''] = gpt;
    const words = rest
      .split('-')
      .filter(Boolean)
      .map((word) => (/^[a-z]+$/i.test(word) ? titleCase(word) : word));
    return ['GPT', version, ...words].join('-');
  }
  return trimmed;
}
